import type { Clip } from './types';

export const THUMBNAIL_INTERVAL_S = 2;

export const createNewClip = (base: Partial<Clip>): Clip => {
    const isVisual = base.type === 'video' || base.type === 'image' || base.type === 'text' || base.type === 'zoom' || base.type === 'synthe' || base.type === 'panel';
    const isZoom = base.type === 'zoom';
    return {
        id: `clip-${Date.now()}-${Math.random()}`,
        name: "New Clip",
        type: 'video',
        trackId: '',
        start: 0,
        duration: 5,
        originalDuration: 5,
        trimStart: 0,
        transform: isVisual ? { x: 0, y: 0, scale: 1, rotation: 0 } : null,
        endTransform: isZoom ? { x: 0, y: 0, scale: 1, rotation: 0 } : undefined,
        opacity: 1,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        volume: 1,
        clipRole: undefined,
        ...base,
    };
};

export const getMediaDurationFromUrl = (url: string): Promise<number> => new Promise((resolve) => {
    // FIX: Use `window.document` to access the DOM.
    const media = window.document.createElement('video');
    media.src = url;
    media.crossOrigin = "anonymous";
    media.onloadedmetadata = () => { resolve(media.duration); };
    media.onerror = () => { resolve(5); }; // Default duration on error
});

export const generateThumbnails = (videoUrl: string): Promise<string[]> => new Promise((resolve) => {
    if (!videoUrl) {
        console.warn("[Thumbnails] Empty videoUrl provided, aborting thumbnail generation.");
        return resolve([]);
    }

    const isImage = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(videoUrl);
    if (isImage) {
        // Just return the image URL itself as the only thumbnail
        return resolve([videoUrl]);
    }

    // FIX: Use `window.document` to access the DOM.
    const video = window.document.createElement("video");

    if (!videoUrl.startsWith('blob:')) {
        video.crossOrigin = "anonymous";
    }

    // FIX: Encode spaces in URL - encodeURI handles unencoded characters while preserving already-encoded ones
    const safeUrl = videoUrl.startsWith('blob:') ? videoUrl : videoUrl.replace(/ /g, '%20');
    video.src = safeUrl;
    video.muted = true;
    video.playsInline = true;

    // Timeout fallback (15 seconds - generating multiple thumbnails takes time)
    const timeoutId = setTimeout(() => {
        console.warn("[Thumbnails] Generation timed out for:", videoUrl);
        // Clean up safely
        video.onloadedmetadata = null;
        video.onerror = null;
        video.removeAttribute("src");
        resolve([]);
    }, 15000);

    video.onloadedmetadata = async () => {
        const canvas = window.document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!context) {
            clearTimeout(timeoutId);
            resolve([]);
            return;
        }

        const thumbnails: string[] = [];
        canvas.width = 160;
        canvas.height = (video.videoHeight / video.videoWidth) * canvas.width;

        try {
            for (let time = 0; time < video.duration; time += THUMBNAIL_INTERVAL_S) {
                video.currentTime = time;
                await new Promise(res => {
                    const onSeek = () => {
                        video.removeEventListener('seeked', onSeek);
                        res(null);
                    };
                    video.addEventListener('seeked', onSeek);
                    // Backup trigger if seek doesn't happen fast
                    if (video.readyState >= 4) setTimeout(onSeek, 500);
                });

                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                thumbnails.push(canvas.toDataURL("image/jpeg"));
            }
            clearTimeout(timeoutId);
            resolve(thumbnails);
        } catch (e) {
            console.error("Error during thumbnail generation loop:", e);
            clearTimeout(timeoutId);
            resolve(thumbnails); // Return what we have
        } finally {
            // Nullify listeners to avoid spurious errors
            video.onerror = null;
            video.onloadedmetadata = null;

            video.removeAttribute("src");
        }
    };

    video.onerror = (e) => {
        clearTimeout(timeoutId);
        const err = (video as any).error;
        console.error("Failed to load video for thumbnails. URL:", videoUrl, "SafeURL:", video.src, "Error Event:", e, "MediaError:", err, "Code:", err?.code, "Message:", err?.message);
        resolve([]);
    };

    video.load();
});

export const generateWaveformData = async (audioUrl: string): Promise<number[]> => {
    try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const rawData = audioBuffer.getChannelData(0);
        const samples = 120;
        const blockSize = Math.floor(rawData.length / samples);
        const filteredData = [];
        for (let i = 0; i < samples; i++) {
            let blockStart = blockSize * i;
            let sum = 0;
            for (let j = 0; j < blockSize; j++) {
                sum += Math.abs(rawData[blockStart + j] || 0);
            }
            filteredData.push(sum / blockSize);
        }
        const multiplier = Math.pow(Math.max(...filteredData), -1) || 1;
        return filteredData.map(n => n * multiplier);
    } catch (e) { console.error("Error generating waveform:", e); return []; }
};

export const generateThumbnailFromUrl = (videoUrl: string): Promise<string> => new Promise((resolve) => {
    if (!videoUrl) {
        console.warn("[Thumbnail] Empty videoUrl provided, aborting generation.");
        return resolve("");
    }

    const isImage = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(videoUrl);
    if (isImage) {
        return resolve(videoUrl);
    }

    // FIX: Use `window.document` to access the DOM.
    const video = window.document.createElement("video");

    // Only set crossOrigin if NOT a blob URL to avoid tainting issues
    if (!videoUrl.startsWith('blob:')) {
        video.crossOrigin = "anonymous";
    }

    video.playsInline = true;
    video.muted = true;
    // FIX: Encode spaces in URL - encodeURI handles unencoded characters while preserving already-encoded ones
    const safeUrl = videoUrl.startsWith('blob:') ? videoUrl : videoUrl.replace(/ /g, '%20');
    video.src = safeUrl;

    // Timeout fallback (3 seconds)
    const timeoutId = setTimeout(() => {
        console.warn("[Thumbnail] Generation timed out for:", videoUrl);
        // Clean up
        video.onloadedmetadata = null;
        video.onseeked = null;
        video.onerror = null;
        video.removeAttribute("src");
        resolve("");
    }, 3000);

    video.onloadedmetadata = () => {
        const canvas = window.document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) {
            clearTimeout(timeoutId);
            resolve("");
            return;
        }

        // Seek to 1s or 10% of duration (to ensure we have an image, not black frame)
        let seekTime = Math.min(1, video.duration / 2);
        if (video.duration > 0) seekTime = Math.max(0.1, Math.min(1, video.duration * 0.1));

        video.currentTime = seekTime;

        video.onseeked = () => {
            clearTimeout(timeoutId);
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg"));

            // Cleanup - Remove listeners to prevent error triggering on cleanup
            video.onloadedmetadata = null;
            video.onseeked = null;
            video.onerror = null;

            video.removeAttribute("src");
        };
    };

    video.onerror = (e) => {
        clearTimeout(timeoutId);
        const err = (video as any).error;
        console.error("Failed to load video for thumbnail generation. URL:", videoUrl, "SafeURL:", video.src, "Error Event:", e, "MediaError:", err, "Code:", err?.code, "Message:", err?.message);
        resolve("");
    };

    // Trigger load
    video.load();
});

export const extractFirstFrame = (sourceUrl: string): Promise<Blob | null> => new Promise((resolve) => {
    if (!sourceUrl) {
        console.warn("[ExtractFrame] Empty sourceUrl provided.");
        return resolve(null);
    }

    const isImage = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(sourceUrl);

    // For images, we can fetch and return the blob directly or just draw to canvas
    if (isImage) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = window.document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (!ctx) return resolve(null);
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
        };
        img.onerror = () => resolve(null);
        img.src = sourceUrl.startsWith('blob:') ? sourceUrl : sourceUrl.replace(/ /g, '%20');
        return;
    }

    // For videos, extract first frame
    const video = window.document.createElement("video");
    if (!sourceUrl.startsWith('blob:')) {
        video.crossOrigin = "anonymous";
    }
    video.playsInline = true;
    video.muted = true;
    video.src = sourceUrl.startsWith('blob:') ? sourceUrl : sourceUrl.replace(/ /g, '%20');

    const timeoutId = setTimeout(() => {
        video.onloadedmetadata = null;
        video.onseeked = null;
        resolve(null);
    }, 5000);

    video.onloadedmetadata = () => {
        let seekTime = Math.min(1, video.duration / 2);
        if (video.duration > 0) seekTime = Math.max(0.1, Math.min(1, video.duration * 0.1));
        video.currentTime = seekTime;

        video.onseeked = () => {
            clearTimeout(timeoutId);
            const canvas = window.document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            if (!ctx) return resolve(null);

            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                resolve(blob);
                video.removeAttribute("src");
            }, 'image/jpeg', 0.95);
        };
    };

    video.onerror = () => {
        clearTimeout(timeoutId);
        resolve(null);
    };

    video.load();
});

export const floatTo16BitPCM = (float32Array: Float32Array): Int16Array => {
    const output = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return output;
};

export const encodeWav = (samples: Int16Array, sampleRate: number, numChannels: number): Blob => {
    const buffer = new ArrayBuffer(44 + samples.length * 2 * numChannels);
    const view = new DataView(buffer);

    const writeString = (offset: number, string: string) => {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    };

    const bitsPerSample = 16;
    const blockAlign = numChannels * (bitsPerSample / 8);
    const byteRate = sampleRate * blockAlign;
    const dataSize = samples.length * (bitsPerSample / 8);

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    for (let i = 0; i < samples.length; i++) {
        view.setInt16(44 + i * 2, samples[i], true);
    }

    return new Blob([view], { type: 'audio/wav' });
};

// --- Easing Functions for Animations ---

export const easeOutElastic = (x: number): number => {
    const c4 = (2 * Math.PI) / 3;
    return x === 0 ? 0 : x === 1 ? 1 : Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * c4) + 1;
};

export const easeInBack = (x: number): number => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return c3 * x * x * x - c1 * x * x;
};

export const easeInOutCubic = (x: number): number => {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};

export const formatDuration = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds)) {
        return '00:00';
    }

    const totalSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const minuteStr = minutes.toString().padStart(2, '0');
    const secondStr = secs.toString().padStart(2, '0');

    if (hours > 0) {
        return `${hours}:${minuteStr}:${secondStr}`;
    }

    return `${minuteStr}:${secondStr}`;
};

// --- SRT Subtitle Utilities ---

/**
 * Converts seconds to SRT timestamp format (HH:MM:SS,mmm)
 */
export const secondsToSrtTimestamp = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`;
};

/**
 * Converts SRT timestamp (HH:MM:SS,mmm) to seconds
 */
export const srtTimestampToSeconds = (timestamp: string): number => {
    const parts = timestamp.replace(',', '.').split(':');
    if (parts.length !== 3) return 0;

    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseFloat(parts[2]) || 0;

    return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Parses an SRT file content into Subtitle array
 */
export const parseSrtFile = (content: string): { id: number; start: number; end: number; text: string }[] => {
    const subtitles: { id: number; start: number; end: number; text: string }[] = [];
    const blocks = content.trim().split(/\n\s*\n/);

    for (const block of blocks) {
        const lines = block.trim().split('\n');
        if (lines.length < 3) continue;

        const id = parseInt(lines[0], 10);
        if (isNaN(id)) continue;

        const timeLine = lines[1];
        const timeMatch = timeLine.match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);

        if (!timeMatch) continue;

        const start = srtTimestampToSeconds(timeMatch[1]);
        const end = srtTimestampToSeconds(timeMatch[2]);
        const text = lines.slice(2).join('\n');

        subtitles.push({ id, start, end, text });
    }

    return subtitles;
};

/**
 * Converts Subtitle array to SRT file format
 */
export const subtitlesToSrt = (subtitles: { id: number | string; start: number; end: number; text: string }[]): string => {
    return subtitles
        .map((sub, index) => {
            const id = typeof sub.id === 'number' ? sub.id : index + 1;
            const startTime = secondsToSrtTimestamp(sub.start);
            const endTime = secondsToSrtTimestamp(sub.end);
            return `${id}\n${startTime} --> ${endTime}\n${sub.text}`;
        })
        .join('\n\n') + '\n';
};

/**
 * Parses a JSON file content into Subtitle array
 * Supports multiple JSON formats:
 * 1. Simple array: [{ start: 1.5, end: 3.2, text: "Hello" }, ...]
 * 2. Wrapped object: { subtitles: [...] } or { items: [...] }
 * 3. Replicate/Whisper format: { output: { chunks: [{ text: "...", timestamp: [start, end] }] } }
 * 4. String timestamps: { start: "00:00:01,500", end: "00:00:03,200", text: "Hello" }
 * 5. Milliseconds: { startMs: 1500, endMs: 3200, text: "Hello" }
 * 6. Duration format: { start: 1.5, duration: 1.7, text: "Hello" }
 */
export const parseJsonFile = (content: string): { id: number; start: number; end: number; text: string }[] => {
    try {
        const data = JSON.parse(content);

        // Extract array from various nested paths
        let items: any[] = [];

        if (Array.isArray(data)) {
            items = data;
        } else if (data.output?.chunks) {
            // Replicate/Whisper format
            items = data.output.chunks;
        } else if (data.data?.chunks) {
            items = data.data.chunks;
        } else if (data.chunks) {
            items = data.chunks;
        } else if (data.subtitles) {
            items = data.subtitles;
        } else if (data.items) {
            items = data.items;
        } else if (data.segments) {
            items = data.segments;
        } else if (data.results) {
            items = data.results;
        }

        if (!Array.isArray(items) || items.length === 0) {
            throw new Error('No valid subtitle array found in JSON');
        }

        const subtitles: { id: number; start: number; end: number; text: string }[] = [];

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item || typeof item !== 'object') continue;

            // Extract text
            const text = item.text || item.content || item.subtitle || '';
            if (!text) continue;

            let start = 0;
            let end = 0;

            // Handle timestamp array format: timestamp: [start, end]
            if (Array.isArray(item.timestamp) && item.timestamp.length >= 2) {
                start = typeof item.timestamp[0] === 'number' ? item.timestamp[0] : 0;
                end = typeof item.timestamp[1] === 'number' ? item.timestamp[1] : 0;
            } else {
                // Extract start time
                if (typeof item.start === 'number') {
                    start = item.start;
                } else if (typeof item.start === 'string') {
                    // Try to parse as SRT timestamp
                    start = srtTimestampToSeconds(item.start);
                } else if (typeof item.startMs === 'number') {
                    start = item.startMs / 1000;
                } else if (typeof item.startTime === 'number') {
                    start = item.startTime;
                } else if (typeof item.startTime === 'string') {
                    start = srtTimestampToSeconds(item.startTime);
                }

                // Extract end time
                if (typeof item.end === 'number') {
                    end = item.end;
                } else if (typeof item.end === 'string') {
                    // Try to parse as SRT timestamp
                    end = srtTimestampToSeconds(item.end);
                } else if (typeof item.endMs === 'number') {
                    end = item.endMs / 1000;
                } else if (typeof item.endTime === 'number') {
                    end = item.endTime;
                } else if (typeof item.endTime === 'string') {
                    end = srtTimestampToSeconds(item.endTime);
                } else if (typeof item.duration === 'number') {
                    // Calculate end from start + duration
                    end = start + item.duration;
                }
            }

            // Validate times
            if (start < 0 || end <= start) {
                console.warn(`Skipping invalid subtitle at index ${i}: start=${start}, end=${end}`);
                continue;
            }

            // Extract or generate ID
            const id = typeof item.id === 'number' ? item.id : i + 1;

            subtitles.push({ id, start, end, text });
        }

        return subtitles;
    } catch (error) {
        console.error('Error parsing JSON file:', error);
        throw new Error('Invalid JSON format');
    }
};

/**
 * Options pour la détection et division des sous-titres trop longs
 */
export interface SubtitleLengthOptions {
    maxCharsPerLine?: number;      // Caractères max par ligne (défaut: 42)
    maxLines?: number;              // Lignes max par sous-titre (défaut: 2)
    maxDuration?: number;           // Durée max en secondes (défaut: 6)
    minDuration?: number;           // Durée min en secondes (défaut: 1)
    targetCharsPerSecond?: number;  // Vitesse de lecture recommandée (défaut: 17)
}

const DEFAULT_LENGTH_OPTIONS: Required<SubtitleLengthOptions> = {
    maxCharsPerLine: 42,
    maxLines: 2,
    maxDuration: 6,
    minDuration: 1,
    targetCharsPerSecond: 17,
};

/**
 * Détecte si un sous-titre est trop long selon les bonnes pratiques
 */
export const isSubtitleTooLong = (
    subtitle: { start: number; end: number; text: string },
    options: SubtitleLengthOptions = {}
): boolean => {
    const opts = { ...DEFAULT_LENGTH_OPTIONS, ...options };
    const duration = subtitle.end - subtitle.start;
    const textLength = subtitle.text.length;

    // Vérifier la durée
    if (duration > opts.maxDuration) {
        return true;
    }

    // Vérifier le nombre total de caractères
    const maxCharsTotal = opts.maxCharsPerLine * opts.maxLines;
    if (textLength > maxCharsTotal) {
        return true;
    }

    // Vérifier la vitesse de lecture (caractères par seconde)
    const charsPerSecond = textLength / duration;
    if (charsPerSecond > opts.targetCharsPerSecond * 1.3) { // 30% de tolérance
        return true;
    }

    return false;
};

/**
 * Divise un texte en segments pour créer plusieurs sous-titres
 * Essaie de diviser aux ponctuations, sinon aux espaces
 */
const splitTextIntoSegments = (text: string, maxChars: number): string[] => {
    const segments: string[] = [];
    let remainingText = text.trim();

    while (remainingText.length > 0) {
        if (remainingText.length <= maxChars) {
            segments.push(remainingText);
            break;
        }

        // Chercher le meilleur point de coupure dans les maxChars premiers caractères
        const chunk = remainingText.substring(0, maxChars);

        // Essayer de couper à une ponctuation forte (. ! ? ;)
        const strongPuncMatch = chunk.match(/[.!?;]\s+(?!$)/g);
        if (strongPuncMatch) {
            const lastPuncIndex = chunk.lastIndexOf(strongPuncMatch[strongPuncMatch.length - 1]);
            if (lastPuncIndex > maxChars * 0.5) { // Au moins 50% du max
                const cutPoint = lastPuncIndex + strongPuncMatch[strongPuncMatch.length - 1].length;
                segments.push(remainingText.substring(0, cutPoint).trim());
                remainingText = remainingText.substring(cutPoint).trim();
                continue;
            }
        }

        // Sinon, essayer de couper à une virgule
        const commaIndex = chunk.lastIndexOf(', ');
        if (commaIndex > maxChars * 0.5) {
            segments.push(remainingText.substring(0, commaIndex + 1).trim());
            remainingText = remainingText.substring(commaIndex + 1).trim();
            continue;
        }

        // Sinon, couper au dernier espace
        const spaceIndex = chunk.lastIndexOf(' ');
        if (spaceIndex > maxChars * 0.3) { // Au moins 30% du max
            segments.push(remainingText.substring(0, spaceIndex).trim());
            remainingText = remainingText.substring(spaceIndex).trim();
        } else {
            // Pas d'espace trouvé, couper au caractère max (cas rare)
            segments.push(remainingText.substring(0, maxChars));
            remainingText = remainingText.substring(maxChars).trim();
        }
    }

    return segments;
};

/**
 * Divise un sous-titre trop long en plusieurs sous-titres plus courts
 * IMPORTANT: Les segments créés gardent le timing ORIGINAL pour éviter la désynchronisation.
 * L'utilisateur doit ensuite ajuster manuellement les timings en écoutant la vidéo.
 */
export const splitLongSubtitle = (
    subtitle: { id: number | string; start: number; end: number; text: string },
    options: SubtitleLengthOptions = {}
): Array<{ id: number | string; start: number; end: number; text: string }> => {
    const opts = { ...DEFAULT_LENGTH_OPTIONS, ...options };

    // Si le sous-titre n'est pas trop long, le retourner tel quel
    if (!isSubtitleTooLong(subtitle, options)) {
        return [subtitle];
    }

    const maxCharsTotal = opts.maxCharsPerLine * opts.maxLines;
    const segments = splitTextIntoSegments(subtitle.text, maxCharsTotal);

    // Si un seul segment, retourner le sous-titre original
    if (segments.length === 1) {
        return [subtitle];
    }

    // IMPORTANT: On garde le timing original pour tous les segments
    // L'utilisateur devra ajuster manuellement en écoutant la vidéo
    const result: Array<{ id: number | string; start: number; end: number; text: string }> = [];

    segments.forEach((segment, index) => {
        result.push({
            id: typeof subtitle.id === 'number'
                ? (subtitle.id + index * 0.01)  // ID numérique : ajouter décimales
                : `${subtitle.id}_${index}`,     // ID string : ajouter suffixe
            start: subtitle.start,  // ⚠️ Même timing que l'original - À AJUSTER MANUELLEMENT
            end: subtitle.end,      // ⚠️ Même timing que l'original - À AJUSTER MANUELLEMENT
            text: segment,
        });
    });

    return result;
};

/**
 * Divise tous les sous-titres trop longs dans un tableau
 */
export const splitAllLongSubtitles = (
    subtitles: Array<{ id: number | string; start: number; end: number; text: string }>,
    options: SubtitleLengthOptions = {}
): Array<{ id: number | string; start: number; end: number; text: string }> => {
    const result: Array<{ id: number | string; start: number; end: number; text: string }> = [];

    subtitles.forEach(subtitle => {
        const split = splitLongSubtitle(subtitle, options);
        result.push(...split);
    });

    return result;
};

export interface ParsedAiJsonResult<T> {
    data: T;
    cleanJson: string;
    strategy: string;
}

interface ParseAiJsonOptions {
    recoverEditingPlan?: boolean;
}

interface RecoveredEditingPlan {
    chatResponse?: string;
    newClips?: Array<{
        originalClipId: string;
        startTime: number;
        endTime: number;
        reasoning: string;
    }>;
    textReplacements?: Array<{
        targetText: string;
        replacementText: string;
        scope: 'all' | 'subtitles' | 'overlays';
    }>;
}

const JSON_STRING_PATTERN = '((?:\\\\.|[^"\\\\])*)';

const normalizeAiJsonText = (text: string): string => String(text ?? '')
    .replace(/^\uFEFF/, '')
    .replace(/```(?:json)?/gi, '')
    .replace(/```/g, '')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .trim();

const findBalancedJsonBlock = (text: string): string | null => {
    const startIndex = text.search(/[{\[]/);
    if (startIndex === -1) return null;

    const stack: string[] = [];
    let inString = false;
    let escaped = false;

    for (let i = startIndex; i < text.length; i++) {
        const char = text[i];

        if (inString) {
            if (escaped) {
                escaped = false;
                continue;
            }
            if (char === '\\') {
                escaped = true;
                continue;
            }
            if (char === '"') {
                inString = false;
            }
            continue;
        }

        if (char === '"') {
            inString = true;
            continue;
        }

        if (char === '{' || char === '[') {
            stack.push(char);
            continue;
        }

        if (char === '}' || char === ']') {
            const last = stack[stack.length - 1];
            if ((last === '{' && char === '}') || (last === '[' && char === ']')) {
                stack.pop();
                if (stack.length === 0) {
                    return text.slice(startIndex, i + 1).trim();
                }
            } else {
                break;
            }
        }
    }

    return text.slice(startIndex).trim();
};

const sanitizeJsonCandidate = (candidate: string): string => {
    const input = normalizeAiJsonText(candidate);
    let output = '';
    let inString = false;
    let escaped = false;

    for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (inString) {
            if (escaped) {
                output += char;
                escaped = false;
                continue;
            }

            if (char === '\\') {
                const next = input[i + 1];
                if (next && /["\\/bfnrtu]/.test(next)) {
                    output += char;
                    escaped = true;
                } else {
                    output += '\\\\';
                }
                continue;
            }

            if (char === '"') {
                inString = false;
                output += char;
                continue;
            }

            if (char === '\n') {
                output += '\\n';
                continue;
            }

            if (char === '\r') {
                output += '\\r';
                continue;
            }

            if (char === '\t') {
                output += '\\t';
                continue;
            }

            if (char.charCodeAt(0) < 32) {
                output += ' ';
                continue;
            }

            output += char;
            continue;
        }

        if (char === '"') {
            inString = true;
        }

        output += char;
    }

    return output.trim();
};

const removeTrailingCommas = (candidate: string): string =>
    candidate.replace(/,(\s*[}\]])/g, '$1');

const parseErrorPosition = (error: unknown): number | null => {
    const message = error instanceof Error ? error.message : String(error ?? '');
    const match = message.match(/position (\d+)/i);
    if (!match) return null;
    const position = Number(match[1]);
    return Number.isFinite(position) ? position : null;
};

const getContextSnippet = (candidate: string, position: number | null): string => {
    if (position === null) {
        return candidate.slice(0, 200);
    }
    const start = Math.max(0, position - 80);
    const end = Math.min(candidate.length, position + 80);
    return candidate.slice(start, end);
};

const previousSignificantIndex = (text: string, startIndex: number): number => {
    for (let i = startIndex; i >= 0; i--) {
        if (!/\s/.test(text[i])) return i;
    }
    return -1;
};

const nextSignificantIndex = (text: string, startIndex: number): number => {
    for (let i = startIndex; i < text.length; i++) {
        if (!/\s/.test(text[i])) return i;
    }
    return -1;
};

const repairJsonFromParseError = (candidate: string, error: unknown): string | null => {
    const position = parseErrorPosition(error);
    if (position === null) return null;

    const nextIndex = nextSignificantIndex(candidate, position);
    const previousIndex = previousSignificantIndex(candidate, Math.max(0, position - 1));
    const nextChar = nextIndex >= 0 ? candidate[nextIndex] : '';
    const previousChar = previousIndex >= 0 ? candidate[previousIndex] : '';
    const message = error instanceof Error ? error.message : String(error ?? '');

    if (message.includes("Expected ',' or '}' after property value") || message.includes("Expected ',' or ']' after array element")) {
        if (nextIndex >= 0 && (nextChar === '"' || nextChar === '{' || nextChar === '[' || /[-0-9tfn]/.test(nextChar))) {
            if (previousChar && !'{[:,['.includes(previousChar)) {
                return `${candidate.slice(0, nextIndex)},${candidate.slice(nextIndex)}`;
            }
        }
    }

    if (message.includes('Unexpected token') && nextIndex >= 0 && (nextChar === '}' || nextChar === ']')) {
        const previousCommaIndex = previousSignificantIndex(candidate, nextIndex - 1);
        if (previousCommaIndex >= 0 && candidate[previousCommaIndex] === ',') {
            return `${candidate.slice(0, previousCommaIndex)}${candidate.slice(previousCommaIndex + 1)}`;
        }
    }

    return null;
};

const decodeJsonStringFragment = (value: string): string =>
    value
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');

const recoverEditingPlanFromText = (text: string): RecoveredEditingPlan | null => {
    const candidate = normalizeAiJsonText(text);
    if (!candidate) return null;

    const clipPattern = new RegExp(
        `"(?:originalClipId|clipId|clip_id)"\\s*:\\s*"${JSON_STRING_PATTERN}"[\\s\\S]{0,320}?"startTime"\\s*:\\s*(-?\\d+(?:\\.\\d+)?)[\\s\\S]{0,160}?"endTime"\\s*:\\s*(-?\\d+(?:\\.\\d+)?)`,
        'g'
    );
    const recoveredClips: NonNullable<RecoveredEditingPlan['newClips']> = [];
    const seenClipKeys = new Set<string>();
    let clipMatch: RegExpExecArray | null;
    while ((clipMatch = clipPattern.exec(candidate)) !== null) {
        const originalClipId = decodeJsonStringFragment(clipMatch[1]).trim();
        const startTime = Number(clipMatch[2]);
        const endTime = Number(clipMatch[3]);
        if (!originalClipId || !Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
            continue;
        }

        const dedupeKey = `${originalClipId}:${startTime}:${endTime}`;
        if (seenClipKeys.has(dedupeKey)) continue;
        seenClipKeys.add(dedupeKey);

        recoveredClips.push({
            originalClipId,
            startTime,
            endTime,
            reasoning: 'Segment recupere depuis une reponse IA invalide.'
        });
    }
    if (recoveredClips.length > 0) {
        return { newClips: recoveredClips };
    }

    const textReplacementPattern = new RegExp(
        `"targetText"\\s*:\\s*"${JSON_STRING_PATTERN}"[\\s\\S]{0,200}?"replacementText"\\s*:\\s*"${JSON_STRING_PATTERN}"[\\s\\S]{0,120}?"scope"\\s*:\\s*"(all|subtitles|overlays)"`,
        'g'
    );
    const recoveredTextReplacements: NonNullable<RecoveredEditingPlan['textReplacements']> = [];
    let replacementMatch: RegExpExecArray | null;
    while ((replacementMatch = textReplacementPattern.exec(candidate)) !== null) {
        recoveredTextReplacements.push({
            targetText: decodeJsonStringFragment(replacementMatch[1]),
            replacementText: decodeJsonStringFragment(replacementMatch[2]),
            scope: replacementMatch[3] as 'all' | 'subtitles' | 'overlays'
        });
    }
    if (recoveredTextReplacements.length > 0) {
        return { textReplacements: recoveredTextReplacements };
    }

    const chatResponsePattern = new RegExp(`"chatResponse"\\s*:\\s*"${JSON_STRING_PATTERN}"`);
    const chatResponseMatch = candidate.match(chatResponsePattern);
    if (chatResponseMatch?.[1]) {
        return { chatResponse: decodeJsonStringFragment(chatResponseMatch[1]) };
    }

    return null;
};

export const extractJsonCandidateFromText = (text: string): string => {
    const normalized = normalizeAiJsonText(text);
    if (!normalized) return '';
    if (normalized.startsWith('{') || normalized.startsWith('[')) return normalized;
    return findBalancedJsonBlock(normalized) || normalized;
};

export const parseAiJsonResponse = <T = any>(
    text: string,
    options: ParseAiJsonOptions = {}
): ParsedAiJsonResult<T> => {
    const cleanJson = extractJsonCandidateFromText(text);
    if (!cleanJson) {
        throw new Error('Reponse IA vide.');
    }

    const candidates = [
        { strategy: 'direct', value: cleanJson },
        { strategy: 'sanitized', value: sanitizeJsonCandidate(cleanJson) },
        { strategy: 'sanitized-without-trailing-commas', value: removeTrailingCommas(sanitizeJsonCandidate(cleanJson)) }
    ].filter((candidate, index, all) =>
        candidate.value && all.findIndex(other => other.value === candidate.value) === index
    );

    let lastError: unknown = null;

    for (const candidate of candidates) {
        try {
            return {
                data: JSON.parse(candidate.value) as T,
                cleanJson: candidate.value,
                strategy: candidate.strategy
            };
        } catch (error) {
            lastError = error;
        }

        let repairedCandidate = candidate.value;
        for (let attempt = 0; attempt < 3; attempt++) {
            const nextCandidate = repairJsonFromParseError(repairedCandidate, lastError);
            if (!nextCandidate || nextCandidate === repairedCandidate) {
                break;
            }

            repairedCandidate = nextCandidate;

            try {
                return {
                    data: JSON.parse(repairedCandidate) as T,
                    cleanJson: repairedCandidate,
                    strategy: `${candidate.strategy}-repaired-${attempt + 1}`
                };
            } catch (error) {
                lastError = error;
            }
        }
    }

    if (options.recoverEditingPlan) {
        const recoveredPlan = recoverEditingPlanFromText(cleanJson) || recoverEditingPlanFromText(text);
        if (recoveredPlan) {
            return {
                data: recoveredPlan as T,
                cleanJson,
                strategy: 'editing-plan-recovery'
            };
        }
    }

    const position = parseErrorPosition(lastError);
    const errorMessage = lastError instanceof Error ? lastError.message : 'Unknown JSON parse error';
    throw new Error(`${errorMessage}. Extrait: ${getContextSnippet(cleanJson, position)}`);
};
